/**
 * Dialer Sound Effects Module
 * Generates DTMF tones and call sounds using Web Audio API
 */

class DialerSounds {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    
    // DTMF frequency pairs (standard telephony frequencies)
    this.dtmfFrequencies = {
      '1': [697, 1209],
      '2': [697, 1336],
      '3': [697, 1477],
      '4': [770, 1209],
      '5': [770, 1336],
      '6': [770, 1477],
      '7': [852, 1209],
      '8': [852, 1336],
      '9': [852, 1477],
      '*': [941, 1209],
      '0': [941, 1336],
      '#': [941, 1477]
    };
  }

  /**
   * Initialize AudioContext (must be called after user interaction)
   */
  init() {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.warn('Web Audio API not supported:', e);
        this.isEnabled = false;
      }
    }
    return this;
  }

  /**
   * Enable/disable sounds
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  /**
   * Resume AudioContext if suspended (needed for some browsers)
   */
  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Play DTMF tone for a digit
   * @param {string} digit - The digit pressed (0-9, *, #)
   * @param {number} duration - Tone duration in milliseconds
   */
  playDTMF(digit, duration = 100) {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    const frequencies = this.dtmfFrequencies[digit];
    if (!frequencies) return;

    this.resume();

    const [lowFreq, highFreq] = frequencies;
    const now = this.audioContext.currentTime;
    const endTime = now + duration / 1000;

    // Create oscillators for dual-tone
    const lowOsc = this.audioContext.createOscillator();
    const highOsc = this.audioContext.createOscillator();
    
    // Create gain nodes for volume control
    const lowGain = this.audioContext.createGain();
    const highGain = this.audioContext.createGain();
    const masterGain = this.audioContext.createGain();

    // Set frequencies
    lowOsc.frequency.value = lowFreq;
    highOsc.frequency.value = highFreq;
    lowOsc.type = 'sine';
    highOsc.type = 'sine';

    // Set volumes (slightly lower to prevent distortion)
    lowGain.gain.value = 0.3;
    highGain.gain.value = 0.3;
    masterGain.gain.value = 0.5;

    // Fade in/out to prevent clicks
    masterGain.gain.setValueAtTime(0, now);
    masterGain.gain.linearRampToValueAtTime(0.5, now + 0.01);
    masterGain.gain.setValueAtTime(0.5, endTime - 0.01);
    masterGain.gain.linearRampToValueAtTime(0, endTime);

    // Connect audio graph
    lowOsc.connect(lowGain);
    highOsc.connect(highGain);
    lowGain.connect(masterGain);
    highGain.connect(masterGain);
    masterGain.connect(this.audioContext.destination);

    // Start and stop
    lowOsc.start(now);
    highOsc.start(now);
    lowOsc.stop(endTime);
    highOsc.stop(endTime);
  }

  /**
   * Play a soft click sound
   */
  playClick() {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    this.resume();

    const now = this.audioContext.currentTime;
    
    // Create a short noise burst for click effect
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.frequency.value = 1800;
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc.start(now);
    osc.stop(now + 0.05);
  }

  /**
   * Play call connecting sound (ringing tone)
   */
  playRingback(duration = 1000) {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    this.resume();

    const now = this.audioContext.currentTime;
    const endTime = now + duration / 1000;
    
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    // Standard ringback tone frequency
    osc.frequency.value = 440;
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.3, now + 0.02);
    gain.gain.setValueAtTime(0.3, endTime - 0.02);
    gain.gain.linearRampToValueAtTime(0, endTime);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc.start(now);
    osc.stop(endTime);
  }

  /**
   * Play call connected sound (short beep)
   */
  playConnected() {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    this.resume();

    const now = this.audioContext.currentTime;
    
    // Two-tone "connected" beep
    const osc1 = this.audioContext.createOscillator();
    const osc2 = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc1.frequency.value = 523.25; // C5
    osc2.frequency.value = 659.25; // E5
    osc1.type = 'sine';
    osc2.type = 'sine';
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gain.gain.setValueAtTime(0.3, now + 0.15);
    gain.gain.linearRampToValueAtTime(0, now + 0.2);
    
    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc1.start(now);
    osc2.start(now + 0.1);
    osc1.stop(now + 0.15);
    osc2.stop(now + 0.2);
  }

  /**
   * Play call ended sound
   */
  playHangup() {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    this.resume();

    const now = this.audioContext.currentTime;
    
    // Descending tone for hangup
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.linearRampToValueAtTime(300, now + 0.15);
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.15);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc.start(now);
    osc.stop(now + 0.15);
  }

  /**
   * Play error/failure sound
   */
  playError() {
    if (!this.isEnabled || !this.audioContext) {
      this.init();
      if (!this.audioContext) return;
    }

    this.resume();

    const now = this.audioContext.currentTime;
    
    // Buzzer-like error sound
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.frequency.value = 200;
    osc.type = 'square';
    
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.3);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc.start(now);
    osc.stop(now + 0.3);
  }
}

// Export singleton instance
const dialerSounds = new DialerSounds();
export default dialerSounds;
